import { AxiosError } from 'axios';
import { FC, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import FullScreenLoading from '../../components/FullScreenLoading/FullScreenLoading';
import { ToastTypes } from '../../components/Toast/Toast.types';
import { getToken } from '../../services/auth/auth.service';
import genericErrorShowToastOptions from '../../utils/genericErrorMessage/genericErrorMessage';
import useLocalUser from '../../utils/useLocalUser/useLocalUser';
import useNavigateWithToast from '../../utils/useNavigateWithToast/useNavigateWithToast';
import { useTranslation } from 'react-i18next';
import { useSTKContext } from '@/context/context';

const CognitoCallback: FC = () => {
  // check for code in the url
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const navigate = useNavigate();
  const navigateWithToast = useNavigateWithToast();
  const { setUser } = useLocalUser();
  const { apiClient } = useSTKContext();

  useEffect(() => {
    // if code, fetch token
    const controller = new AbortController();
    if (code) {
      const fetchTokens = async (authCode: string) => {
        try {
          const response = await getToken(authCode, controller, apiClient);
          if (response) {
            const { accessToken, refreshToken, email, firstName, lastName, loginId, userRole, userType, orgUserCount } =
              response;
            apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            // store the tokens in localStorage
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            // store user info in global state
            setUser({
              email,
              firstName,
              lastName,
              loginId,
              role: userRole,
              type: userType,
              orgUserCount,
            });
            navigate('/', { replace: true });
          }
        } catch (error) {
          const typedError = error as AxiosError;
          if (typedError.message === 'Request failed with status code 404') {
            navigateWithToast('/login', {
              label: t('cognitoCallback.userNotFound'),
              subtext: t('cognitoCallback.subtext'),
              buttonLabel: t('cognitoCallback.buttonLabel'),
              onButtonClick: () => window.location.replace('https://iam.yum.com/uram/getAccess'),
              toastType: ToastTypes.Error,
              id: 'loginError',
            });
          } else {
            navigateWithToast('/login', genericErrorShowToastOptions);
          }
        }
      };
      fetchTokens(code);
    }
    return () => controller.abort();
  }, [code, navigate, setUser, navigateWithToast]);

  if (!code) {
    return <Navigate to="/login" replace />;
  }

  return <FullScreenLoading />;
};

export default CognitoCallback;
